import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Gift, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr } from './shared';

const r2 = (n) => Math.round(n * 100) / 100;

export const discountFor = (o, total) => {
  let amt = o.kind === 'pct' ? (total * o.value) / 100 : o.value;
  if (o.cap) amt = Math.min(amt, o.cap);
  return r2(Math.max(0, Math.min(amt, total)));
};

const lineGross = (it, gst) => {
  const target = Number(it.line_total_target) || 0;
  if (gst && target > 0) return target;
  const base = (Number(it.qty) || 0) * (Number(it.rate) || 0) * (1 - (Number(it.discount_pct) || 0) / 100);
  return gst ? base * (1 + (Number(it.gst_rate) || 0) / 100) : base;
};

// Returns { items, discount }. GST docs: spread the discount across lines via Disc% (tax stays right);
// simple docs: add one negative "Win-back offer" line.
export const applyOffer = (items, offer, { gst, makeItem }) => {
  const total = items.reduce((s, it) => s + lineGross(it, gst), 0);
  const discount = discountFor(offer, total);
  if (!discount || !total) return { items, discount: 0 };
  if (!gst) return { items: [...items, { ...makeItem(), name: `Win-back offer ${offer.code}`, qty: 1, rate: -discount }], discount };
  const keep = 1 - discount / total;
  return {
    discount,
    items: items.map((it) => {
      const target = Number(it.line_total_target) || 0;
      if (target > 0) return { ...it, line_total_target: r2(target * keep) };
      return { ...it, discount_pct: r2(100 - (100 - (Number(it.discount_pct) || 0)) * keep) };
    }),
  };
};

export const WinbackPanel = ({ customerId, applied, onApply }) => {
  const { authHeader } = useAuth();
  const [offers, setOffers] = useState([]);
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setOffers([]);
    if (!customerId || applied) return;
    axios.get(`${API}/winback/available`, { headers: authHeader(), params: { customer_id: customerId } })
      .then((r) => setOffers(r.data.offers)).catch(() => {});
  }, [authHeader, customerId, applied]);

  if (applied) {
    return (
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="winback-applied">
        <span><Gift size={13} className="mr-1 inline" />Win-back code <b>{applied}</b> applied. It is marked used when the invoice is saved.</span>
        <button type="button" onClick={() => onApply(null)} className="font-semibold underline" title="Removes the code; adjust or delete the discount on the lines yourself" data-testid="winback-remove">Remove code</button>
      </div>
    );
  }
  if (!customerId) return null;
  const lookup = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await axios.get(`${API}/winback/code/${encodeURIComponent(code.trim())}`, { headers: authHeader(), params: { customer_id: customerId } });
      onApply(data.offer); setCode('');
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-2" data-testid="winback-panel">
      {offers.map((o) => (
        <div key={o.code} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={`winback-available-${o.code}`}>
          <span><Gift size={13} className="mr-1 inline" />Win-back code available: <b>{o.code}</b> · {o.label} · valid till {o.expires_at.slice(0, 10)}</span>
          <button type="button" onClick={() => onApply(o)} className="rounded-full bg-amber-500 px-3 py-1 font-semibold" style={{ color: '#fff' }} data-testid={`winback-apply-${o.code}`}>Apply</button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Have a code? WB-…" className="w-40 rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs" data-testid="winback-code-input" />
        <button type="button" disabled={!code.trim() || busy} onClick={lookup} className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold disabled:opacity-50" data-testid="winback-code-apply">
          {busy ? <Loader2 size={12} className="animate-spin" /> : 'Apply code'}
        </button>
        {err && <span className="text-xs text-rose-600" data-testid="winback-code-error">{err}</span>}
      </div>
    </div>
  );
};
